'use client'
import * as React from 'react'

import { isCommit } from '@basehub/data/replicache/index'
import { UseDiffResult, useDiff } from './use-diff'
import { useCommit } from '../../hooks-data/use-commit'
import { useCommitBlockTreeManager } from '~/hooks-data/blocks-tree'
import {
  useWorkingTreeStore,
  useWorkingTreeStoreRoot,
} from '~/hooks-data/blocks-tree/provider'
import { useSelectedBranchOrCommitQuery } from '../commits/use-selected-branch-or-commit'
import { createStore, useStore } from 'zustand'
import { useClientDebugger } from '~/hooks/use-client-debugger'
import { useRepoUndoManager } from '~/replicache/schema/repo-space/undo-redo/manager'
import debounce from 'lodash/debounce'

const createWorkingDiffStore = (initial: UseDiffResult) => {
  return createStore<UseDiffResult>()(() => initial)
}

type WorkingDiffStore = ReturnType<typeof createWorkingDiffStore>

export const WorkingDiffContext = React.createContext<
  WorkingDiffStore | undefined
>(undefined)

export const WorkingDiffProvider = ({
  children,
}: {
  children?: React.ReactNode
}) => {
  const { data: selectedBranchOrCommit } = useSelectedBranchOrCommitQuery()

  const { headCommitId } = React.useMemo(() => {
    if (!selectedBranchOrCommit) return {}
    if (isCommit(selectedBranchOrCommit)) {
      return {}
    } else {
      return {
        headCommitId: selectedBranchOrCommit.headCommitId,
      }
    }
  }, [selectedBranchOrCommit])

  const { data, isLoading: isLoadingHeadCommit } = useCommit(headCommitId)
  const headCommit = data?.result

  const hasHeadCommit = isLoadingHeadCommit
    ? Boolean(headCommit) || undefined
    : Boolean(headCommit)

  const workingTreeStore = useWorkingTreeStoreRoot()
  const workingTree = useWorkingTreeStore((s) => s.tree)
  const workingTreeRootLocalLastUpdatedAt = useWorkingTreeStore(
    (s) => s.rootLocalLastUpdatedAt
  )
  const { data: headCommitBlockTree } = useCommitBlockTreeManager({
    commitId: headCommit?.id,
    rootBlockId: headCommit?.rootBlockId ?? undefined,
    caller: 'use-working-diff',
  })

  const [localLastUpdatedAt, setLocalLastUpdatedAt] = React.useState(
    workingTreeRootLocalLastUpdatedAt.current
  )

  const workingDiffKey = React.useMemo(() => {
    // intentionally not include hash so it doesn't invalidate every time the user types
    return [
      'working-diff',
      workingTree?.root.key,
      headCommitId,
      localLastUpdatedAt,
    ]
  }, [workingTree?.root.key, headCommitId, localLastUpdatedAt])

  const result = useDiff({
    keyOverride: workingDiffKey,
    treeNew: workingTree ?? undefined,
    treeOld:
      hasHeadCommit || hasHeadCommit === undefined
        ? headCommitBlockTree
        : false,
    treeCommonAncestor: false,
  })

  const { isDebugging } = useClientDebugger()

  if (isDebugging) {
    console.log('workingDiffKey', workingDiffKey)
    console.log('workingTree', workingTree)
    console.log('headCommitBlockTree', headCommitBlockTree)
    console.log('headCommit', headCommit)
    console.log('result', result)
  }

  const undoManager = useRepoUndoManager()

  // refetch when hash changes with a debouncer
  React.useEffect(() => {
    let unsubUndo: (() => void) | undefined
    let unsubRedo: (() => void) | undefined
    if (undoManager.current) {
      unsubUndo = undoManager.current.on('undo', () => {
        setLocalLastUpdatedAt(new Date().toISOString())
      })
      unsubRedo = undoManager.current.on('redo', () => {
        setLocalLastUpdatedAt(new Date().toISOString())
      })
    }
    return () => {
      if (unsubUndo) {
        unsubUndo()
      }
      if (unsubRedo) {
        unsubRedo()
      }
    }
  }, [headCommit?.id, undoManager])

  React.useEffect(() => {
    const updater = debounce((lastUpdatedAt: string) => {
      setLocalLastUpdatedAt(lastUpdatedAt)
    }, 500)

    const unsub = workingTreeStore.subscribe((state) => {
      if (state.rootLocalLastUpdatedAt.current) {
        updater(state.rootLocalLastUpdatedAt.current)
      }
    })

    return () => {
      unsub()
      updater.cancel()
    }
  }, [workingTreeStore])

  const storeRef = React.useRef(createWorkingDiffStore(result))

  // keep store in sync with the result
  React.useEffect(() => {
    if (!result.diff) return
    storeRef.current.setState(result)
  }, [result])

  return (
    // eslint-disable-next-line react-compiler/react-compiler
    <WorkingDiffContext.Provider value={storeRef.current}>
      {children}
    </WorkingDiffContext.Provider>
  )
}

export function useWorkingDiff() {
  const store = React.useContext(WorkingDiffContext)
  if (!store) throw new Error('Missing WorkingDiffContext.Provider in the tree')
  return useStore(store)
}
